<template>
  <div class="main-container p-3">
    <KidHeader :profileId="profileId" :serialNumber="serialNumber" />

    <!-- Back to Geofence List -->
    <div class="back-home text-left mb-3">
      <a class="text-black" href="javascript:void(0)" @click="goBack">
        <i class="fas fa-arrow-left"></i> <b>Back to Geofences</b>
      </a>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="loading-container">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Loading...</span>
      </div>
      <p class="mt-3 text-muted">Loading history...</p>
    </div>

    <!-- Content (shown when not loading) -->
    <template v-else>
      <!-- Page Header -->
      <div class="schedule-header text-left">
        <div class="header-content">
          <div class="header-text">
            <h4 class="mb-2">
              <i class="fas fa-history mr-2"></i>
              {{ geofenceName ? geofenceName + ' History' : 'All Geofence History' }}
            </h4>
            <p class="mb-0">View all entry and exit events</p>
          </div>
        </div>
      </div>

      <!-- Filter Options -->
      <div class="filter-section mb-3" v-if="!geofenceId">
        <div class="row">
          <div class="col-md-6 mb-2">
            <label class="font-weight-bold"><small>Filter by Event Type</small></label>
            <select class="form-control form-control-sm" v-model="filterEventType" @change="applyFilters">
              <option value="">All Events</option>
              <option value="entry">Entry Only</option>
              <option value="exit">Exit Only</option>
            </select>
          </div>
          <div class="col-md-6 mb-2">
            <label class="font-weight-bold"><small>Filter by Date</small></label>
            <select class="form-control form-control-sm" v-model="filterDate" @change="applyFilters">
              <option value="">All Time</option>
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
            </select>
          </div>
        </div>
      </div>

      <!-- Stats Summary -->
      <div class="stats-container" v-if="historyEvents.length > 0">
        <div class="stat-card">
          <div class="stat-icon bg-success">
            <i class="fas fa-arrow-right"></i>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ entryCount }}</div>
            <div class="stat-label">Entries</div>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon bg-danger">
            <i class="fas fa-arrow-left"></i>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ exitCount }}</div>
            <div class="stat-label">Exits</div>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon bg-info">
            <i class="fas fa-list"></i>
          </div>
          <div class="stat-content">
            <div class="stat-value">{{ historyEvents.length }}</div>
            <div class="stat-label">Total Events</div>
          </div>
        </div>
      </div>

      <!-- History List -->
      <div class="schedule-list-container" :style="{ 'width': '100%'}">
        <!-- Empty State -->
        <div v-if="historyEvents.length === 0" class="empty-state-modern">
          <div class="empty-icon">
            <i class="fas fa-history"></i>
          </div>
          <h5 class="empty-title">No History Yet</h5>
          <p class="empty-text">No geofence events recorded{{ geofenceName ? ' for ' + geofenceName : '' }}</p>
          <button class="btn btn-primary" @click="goBack">
            <i class="fas fa-arrow-left mr-1"></i>Back to Geofences
          </button>
        </div>

        <!-- History List Items -->
        <div v-else class="schedules-list text-left">
          <div 
            v-for="event in historyEvents" 
            :key="event._id" 
            class="schedule-item"
          >
            <!-- Event Header -->
            <div class="schedule-item-header">
              <div class="schedule-title-section">
                <div class="schedule-icon" :class="event.eventType === 'entry' ? 'icon-entry' : 'icon-exit'">
                  <i :class="event.eventType === 'entry' ? 'fas fa-arrow-right' : 'fas fa-arrow-left'"></i>
                </div>
                <div class="schedule-title-info">
                  <h5 class="schedule-name">{{ event.geofenceName }}</h5>
                  <div class="schedule-meta">
                    <span class="badge-modern" :class="event.eventType === 'entry' ? 'badge-entry-type' : 'badge-exit-type'">
                      <i :class="event.eventType === 'entry' ? 'fas fa-arrow-right' : 'fas fa-arrow-left'"></i>
                      {{ event.eventType === 'entry' ? 'Entry' : 'Exit' }}
                    </span>
                    <span class="schedule-status-text text-muted">
                      <i class="far fa-clock"></i> {{ formatDateTime(event.eventTime) }}
                    </span>
                  </div>
                </div>
              </div>
              <div class="schedule-actions">
                <span class="notification-badge" :class="event.notificationSent ? 'sent' : 'not-sent'">
                  <i :class="event.notificationSent ? 'fas fa-bell' : 'fas fa-bell-slash'"></i>
                  {{ event.notificationSent ? 'Notified' : 'Not Notified' }}
                </span>
              </div>
            </div>

            <!-- Event Content -->
            <div class="schedule-item-content">
              <!-- Location Section -->
              <div class="schedule-section" v-if="event.location">
                <div class="section-label">
                  <i class="fas fa-map-pin"></i>
                  <span>Geofence Location</span>
                </div>
                <div class="location-address">
                  {{ event.location.address || 'Address not available' }}
                </div>
                <div class="location-coords" v-if="event.location.latitude && event.location.longitude">
                  <i class="fas fa-compass"></i>
                  {{ event.location.latitude.toFixed(6) }}, {{ event.location.longitude.toFixed(6) }}
                </div>
              </div>

              <!-- Device Location Section -->
              <div class="schedule-section" v-if="event.deviceLocation">
                <div class="section-label">
                  <i class="fas fa-mobile-alt"></i>
                  <span>Device Location</span>
                </div>
                <div class="time-slots-list">
                  <div class="time-slot-item device-location-item">
                    <div class="time-slot-left">
                      <div class="time-slot-icon icon-device">
                        <i class="fas fa-map-marker-alt"></i>
                      </div>
                      <div class="time-slot-info">
                        <div class="time-range">
                          <strong>Device Position</strong>
                        </div>
                        <div class="time-slot-label" v-if="event.deviceLocation.latitude && event.deviceLocation.longitude">
                          {{ event.deviceLocation.latitude.toFixed(6) }}, {{ event.deviceLocation.longitude.toFixed(6) }}
                        </div>
                        <div class="time-slot-label" v-else>
                          Coordinates not available
                        </div>
                      </div>
                    </div>
                    <div class="time-slot-right" v-if="event.distanceFromCenter">
                      <span class="mode-badge mode-distance">
                        <i class="fas fa-ruler"></i>
                        {{ event.distanceFromCenter }}m
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Event Details -->
              <div class="schedule-section">
                <div class="section-label">
                  <i class="fas fa-info-circle"></i>
                  <span>Event Details</span>
                </div>
                <div class="time-slots-list">
                  <div class="time-slot-item info-item">
                    <div class="time-slot-left">
                      <div class="time-slot-icon icon-time">
                        <i class="far fa-clock"></i>
                      </div>
                      <div class="time-slot-info">
                        <div class="time-range">
                          <strong>Event Time</strong>
                        </div>
                        <div class="time-slot-label">
                          {{ formatFullDateTime(event.eventTime) }}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="time-slot-item info-item" v-if="event.notificationSent">
                    <div class="time-slot-left">
                      <div class="time-slot-icon icon-notification">
                        <i class="fas fa-bell"></i>
                      </div>
                      <div class="time-slot-info">
                        <div class="time-range">
                          <strong>Notification Sent</strong>
                        </div>
                        <div class="time-slot-label">
                          {{ formatFullDateTime(event.notificationSentAt) }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="totalPages > 1" class="pagination-container">
        <button 
          class="btn btn-secondary btn-sm" 
          @click="prevPage" 
          :disabled="currentPage === 1"
        >
          <i class="fas fa-chevron-left"></i> Previous
        </button>
        <span class="pagination-info">
          Page {{ currentPage }} of {{ totalPages }}
        </span>
        <button 
          class="btn btn-secondary btn-sm" 
          @click="nextPage" 
          :disabled="currentPage === totalPages"
        >
          Next <i class="fas fa-chevron-right"></i>
        </button>
      </div>
    </template>

    <KidFooter :profileId="profileId" :serialNumber="serialNumber" />
  </div>
</template>

<script>
import GeofenceService from "@/services/GeofenceService";
import moment from "moment";
import KidHeader from "@/components/KidHeader.vue";
import KidFooter from "@/components/KidFooter.vue";

export default {
  name: "GeofenceHistory",
  components: {
    KidHeader,
    KidFooter
  },
  data() {
    return {
      profileId: this.$route.query.profileId || "",
      serialNumber: this.$route.query.serialNumber || "",
      geofenceId: this.$route.query.geofenceId || null,
      geofenceName: this.$route.query.geofenceName || "",
      
      historyEvents: [],
      isLoading: false,
      
      // Filters
      filterEventType: "",
      filterDate: "",
      
      // Pagination
      currentPage: 1,
      itemsPerPage: 20,
      totalPages: 1
    };
  },
  computed: {
    entryCount() {
      return this.historyEvents.filter(e => e.eventType === 'entry').length;
    },
    exitCount() {
      return this.historyEvents.filter(e => e.eventType === 'exit').length;
    }
  },
  mounted() {
    this.loadHistory();
  },
  methods: {
    async loadHistory() {
      this.isLoading = true;
      try {
        let response;
        
        if (this.geofenceId) {
          // Load history for specific geofence
          response = await GeofenceService.getGeofenceHistory(
            this.geofenceId,
            {
              profileId: this.profileId,
              serialNumber: this.serialNumber,
              page: this.currentPage,
              limit: this.itemsPerPage
            }
          );
        } else {
          // Load all history
          const params = {
            profileId: this.profileId,
            serialNumber: this.serialNumber,
            page: this.currentPage,
            limit: this.itemsPerPage
          };
          
          // Add optional filters only if they have values
          if (this.filterEventType) {
            params.eventType = this.filterEventType;
          }
          
          if (this.filterDate) {
            params.dateFilter = this.filterDate;
          }
          
          response = await GeofenceService.getAllHistory(params);
        }

        if (response.data.success) {
          // Backend returns data.data.history or data.data depending on endpoint
          this.historyEvents = response.data.data?.history || response.data.data || [];
          
          // Handle pagination from backend
          const pagination = response.data.data?.pagination || response.data.pagination;
          if (pagination) {
            this.totalPages = pagination.totalPages || 1;
            this.currentPage = pagination.currentPage || 1;
          }
          
          // Update geofence name if available (for specific geofence history)
          if (response.data.data?.geofence?.name && !this.geofenceName) {
            this.geofenceName = response.data.data.geofence.name;
          }
        }
      } catch (error) {
        console.error("Error loading history:", error);
        this.$toast.error("Failed to load history");
      } finally {
        this.isLoading = false;
      }
    },

    applyFilters() {
      this.currentPage = 1;
      this.loadHistory();
    },

    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.loadHistory();
      }
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.loadHistory();
      }
    },

    formatDateTime(time) {
      if (!time) return "";
      return moment(time).fromNow();
    },

    formatFullDateTime(time) {
      if (!time) return "";
      return moment(time).format("MMM DD, YYYY h:mm A");
    },

    goBack() {
      this.$router.push({
        path: "/geofence",
        query: {
          profileId: this.profileId,
          serialNumber: this.serialNumber
        }
      });
    }
  }
};
</script>

<style scoped>
.back-home {
  display: block;
  width: 100%;
  margin-bottom: 15px;
  font-size: 1rem;
}

/* Loading Container */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.loading-container .spinner-border {
  width: 3rem;
  height: 3rem;
}

/* Schedule Header */
.schedule-header {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 50%, #b45309 100%);
  padding: 15px;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(245, 158, 11, 0.2);
  margin-bottom: 25px;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-text h4 {
  color: white;
  font-weight: 700;
  font-size: 1.8rem;
  margin: 0;
}

.header-text p {
  color: rgba(255, 255, 255, 0.9);
  font-size: 1rem;
}

/* Filter Section */
.filter-section {
  background: white;
  padding: 15px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

/* Stats Container */
.stats-container {
  display: flex;
  gap: 15px;
  margin-bottom: 30px;
  overflow-x: auto;
}

.stat-card {
  background: white;
  padding: 15px 15px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  display: flex;
  align-items: center;
  gap: 15px;
  transition: all 0.3s ease;
  flex: 1;
  min-width: 200px;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
}

.stat-icon {
  width: 35px;
  height: 35px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: white;
  flex-shrink: 0;
}

.stat-icon.bg-success {
  background: linear-gradient(135deg, #10b981, #059669);
}

.stat-icon.bg-danger {
  background: linear-gradient(135deg, #ef4444, #dc2626);
}

.stat-icon.bg-info {
  background: linear-gradient(135deg, #f59e0b, #d97706);
}

.stat-content {
  display: flex;
  flex-direction: column;
}

.stat-value {
  font-size: 1.8rem;
  font-weight: 700;
  color: #1f2937;
  line-height: 1;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 0.9rem;
  color: #6b7280;
  font-weight: 500;
  white-space: nowrap;
}

/* Empty State */
.empty-state-modern {
  background: white;
  padding: 80px 40px;
  border-radius: 16px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.empty-icon {
  width: 100px;
  height: 100px;
  margin: 0 auto 25px;
  background: linear-gradient(135deg, #f3f4f6, #e5e7eb);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-icon i {
  font-size: 3rem;
  color: #9ca3af;
}

.empty-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 10px;
}

.empty-text {
  color: #6b7280;
  font-size: 1.05rem;
  margin-bottom: 30px;
}

/* Schedule List Items */
.schedules-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.schedule-item {
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border: 2px solid transparent;
  overflow: hidden;
}

.schedule-item:hover {
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
  border-color: #f59e0b;
  transform: translateY(-2px);
}

/* Schedule Item Header */
.schedule-item-header {
  padding: 10px;
  background: linear-gradient(to right, #f9fafb, #ffffff);
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.schedule-title-section {
  display: flex;
  align-items: center;
  gap: 15px;
}

.schedule-icon {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  color: white;
}

.schedule-icon.icon-entry {
  background: linear-gradient(135deg, #10b981, #059669);
}

.schedule-icon.icon-exit {
  background: linear-gradient(135deg, #ef4444, #dc2626);
}

.schedule-name {
  font-size: 1.3rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 5px 0;
}

.schedule-meta {
  display: flex;
  align-items: center;
  gap: 12px;
}

.badge-modern {
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.badge-entry-type {
  background: #d1fae5;
  color: #065f46;
}

.badge-exit-type {
  background: #fee2e2;
  color: #991b1b;
}

.schedule-status-text {
  font-size: 0.85rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 5px;
}

/* Notification Badge */
.notification-badge {
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.notification-badge.sent {
  background: #d1fae5;
  color: #065f46;
}

.notification-badge.not-sent {
  background: #f3f4f6;
  color: #6b7280;
}

/* Schedule Content */
.schedule-item-content {
  padding: 25px;
}

.schedule-section {
  margin-bottom: 15px;
}

.schedule-section:last-child {
  margin-bottom: 0;
}

.section-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: #4b5563;
  margin-bottom: 12px;
  font-size: 0.95rem;
}

.section-label i {
  color: #f59e0b;
}

/* Location Info */
.location-address {
  font-size: 0.95rem;
  color: #1f2937;
  margin-bottom: 6px;
  font-weight: 500;
}

.location-coords {
  font-size: 0.85rem;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 6px;
}

/* Time Slots List */
.time-slots-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.time-slot-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  background: #f9fafb;
  border-radius: 10px;
  border-left: 4px solid #d1d5db;
  transition: all 0.2s ease;
}

.time-slot-item:hover {
  background: #f3f4f6;
  transform: translateX(4px);
}

.time-slot-item.device-location-item {
  border-left-color: #3b82f6;
  background: #eff6ff;
}

.time-slot-item.info-item {
  border-left-color: #f59e0b;
  background: #fffbeb;
}

.time-slot-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.time-slot-icon {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
}

.time-slot-icon.icon-device {
  background: #bfdbfe;
  color: #1e40af;
}

.time-slot-icon.icon-time {
  background: #fde68a;
  color: #92400e;
}

.time-slot-icon.icon-notification {
  background: #d1fae5;
  color: #065f46;
}

.time-range {
  font-size: 0.75rem;
  color: #1f2937;
}

.time-slot-label {
  font-size: 0.85rem;
  color: #6b7280;
  margin-top: 2px;
}

.mode-badge {
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.mode-badge.mode-distance {
  background: #3b82f6;
  color: white;
}

/* Pagination */
.pagination-container {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  margin-top: 30px;
  padding: 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.pagination-info {
  font-weight: 600;
  color: #1f2937;
}

/* Responsive */
@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
  
  .stats-container {
    flex-direction: column;
  }
  
  .stat-card {
    min-width: 100%;
  }
  
  .schedule-item-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
  
  .schedule-actions {
    width: 100%;
  }
  
  .notification-badge {
    width: 100%;
    justify-content: center;
  }
  
  .time-slot-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  
  .time-slot-right {
    width: 100%;
  }
  
  .mode-badge {
    display: flex;
    width: 100%;
    justify-content: center;
  }
}
</style>